<template>
  <div v-if="loading">Загрузка квиза...</div>
  <div v-if="error" class="error-message">{{ error }}</div>

  <!-- Динамический компонент для отображения вопроса -->
  <component
    v-if="quizData && !showResults && currentQuestion"
    :is="currentQuestionComponent"
    :question="currentQuestion"
    :selected-answer="userAnswers[String(currentQuestion.id)]"
    :selected-answers="userAnswers[String(currentQuestion.id)]"
    :show-results="showResults"
    :selected-course-type="selectedCourseType"
    ref="currentQuestionComponent"
    @answer-selected="handleAnswerSelected"
    @answers-updated="handleAnswersUpdated"
    @auto-check-answer="handleAutoCheckAnswer"
  />

  <!-- Кнопка навигации/отправки -->
  <div
    class="quiz-button"
    :class="{ 'quiz-button--back': currentQuestion.type === 'emotion_recognition' }"
    v-if="quizData && !showResults && currentQuestion && currentQuestion.type !== 'true_false'">
    <button
      v-if="currentQuestion.type === 'emotion_recognition' && canGoBack"
      @click="handleBackButton"
      class="btn submit-button"
    >
      Назад
    </button>
    <button
      @click="nextQuestionOrSubmit"
      class="btn submit-button"
      :disabled="!isAnswerProvidedForCurrentQuestion && !showResults"
    >
      Далее
    </button>
  </div>

  <!-- Results Block -->
  <div v-if="showResults && submissionDetails" class="results-block">
    <div v-if="submitting">Отправка результатов...</div>
    <div v-if="submitError" class="error-message">{{ submitError }}</div>

    <h3>Квиз завершен!</h3>
    <p>Переходим к следующей теме...</p>
  </div>

  <!-- Feedback Modal -->
  <FeedbackModal
    v-if="isFeedbackMounted"
    :is-visible="isFeedbackActive"
    :feedback="currentQuestionFeedback"
    :mascot-url="feedbackMascotUrl"
    :question="currentQuestion"
    :selected-answers="currentQuestion && (currentQuestion.type === 'emotion_recognition' || currentQuestion.type === 'multiple_choice_text' || currentQuestion.type === 'multiple_choice' || currentQuestion.type === 'multiple_choice_paw') ? userAnswers[currentQuestion.id] : null"
    :emotion-images="fixedEmotionImages"
    :selected-course-type="selectedCourseType"
    @continue="nextQuestionOrSubmit"
  />
</template>

<script>
import axios from 'axios';
import SingleChoice from './question_types/SingleChoice.vue';
import TrueFalse from './question_types/TrueFalse.vue';
import MultipleChoice from './question_types/MultipleChoice.vue';
import EmotionRecognitionQuestion from './question_types/EmotionRecognitionQuestion.vue';
import MatchingQuestion from './question_types/MatchingQuestion.vue';
import FillInTheBlanksQuestion from './question_types/FillInTheBlanksQuestion.vue';
import MultipleChoiceTextQuestion from './question_types/MultipleChoiceTextQuestion.vue';
import PawChoiceQuestion from './question_types/PawChoiceQuestion.vue';
import FeedbackModal from './FeedbackModal/FeedbackModal.vue';
import { getOrCreateDeviceUUID } from '@/utils/uuid'

const DEFAULT_MASCOT_URL = 'https://s3.twcstorage.ru/96a5fb50-69d073b0-a7b3-42f3-975d-79ba376843fd/lesson1/quiz/default.png';

export default {
  name: 'QuizComponent', // Changed from 'Quiz' to 'QuizComponent'
  props: {
    quizRef: {
      type: String,
      required: true,
    },
    currentQuestionIndex: {
      type: Number,
      default: 0,
    },
    selectedCourseType: {
      type: String,
      default: 'cats'
    }
  },
  components: {
    SingleChoice,
    TrueFalse,
    MultipleChoice,
    EmotionRecognitionQuestion,
    MatchingQuestion,
    FillInTheBlanksQuestion,
    MultipleChoiceTextQuestion,
    PawChoiceQuestion,
    FeedbackModal
  },

  data() {
    return {
      loading: false,
      error: null,
      quizData: null,
      userAnswers: {}, // { questionId: answer }
      answeredQuestions: [], // массив id вопросов, на которые ответил пользователь
      userId: getOrCreateDeviceUUID(), // Используем утилиту сразу
      showResults: false,
      submitting: false,
      submitError: null,
      finalScore: null,
      submissionDetails: null, // To store full response from submit, including explanations
      currentQuestionFeedback: null, // { isCorrect: boolean, explanation: string, questionId: string }
      isFeedbackVisible: false,    // Controls visibility of the immediate feedback modal/message
      isFeedbackMounted: false,    // Controls if modal is mounted in DOM
      isFeedbackActive: false,     // Controls if modal has active class for animation
      mascotImage: DEFAULT_MASCOT_URL, // Default mascot image
    }
  },
  watch: {
    quizRef: {
      immediate: true, // Call handler immediately when component is created with initial prop
      handler(newQuizRef) {
        if (newQuizRef) {
          // Reset feedback state when quizRef changes
          this.isFeedbackVisible = false;
          this.isFeedbackMounted = false;
          this.isFeedbackActive = false;
          this.currentQuestionFeedback = null;
          this.fetchQuizData(newQuizRef);
        }
      }
    }
  },
  computed: {
    currentQuestion() {
      if (this.quizData && this.quizData.questions && this.currentQuestionIndex < this.quizData.questions.length) {
        return this.quizData.questions[this.currentQuestionIndex];
      }
      return null;
    },
    currentQuestionComponent() {
      if (!this.currentQuestion) return null;



      switch (this.currentQuestion.type) {
        case 'single_choice_text':
          return 'SingleChoice';
        case 'true_false':
          return 'TrueFalse';
        case 'multiple_choice':
          return 'MultipleChoice';
        case 'emotion_recognition':
          return 'EmotionRecognitionQuestion';
        case 'matching':
          return 'MatchingQuestion';
        case 'fill_in_the_blanks':
          return 'FillInTheBlanksQuestion';
        case 'multiple_choice_text':
          return 'MultipleChoiceTextQuestion';
        case 'multiple_choice_paw':
          return 'PawChoiceQuestion';
        default:
          return 'SingleChoice';
      }
    },
    isAnswerProvidedForCurrentQuestion() {
      if (!this.currentQuestion) return false;
      const currentAnswer = this.userAnswers[String(this.currentQuestion.id)];

      if (Array.isArray(currentAnswer)) {
        if (this.currentQuestion.type === 'fill_in_the_blanks') {
            const numberOfBlanks = this.currentQuestion.text_template.filter(part => part === null).length;
            return currentAnswer.length === numberOfBlanks && currentAnswer.every(ans => ans !== null && ans !== '');
        }
        // Для multiple_choice_image проверяем минимальное количество ответов
        if (this.currentQuestion.type === 'multiple_choice' && this.currentQuestion.minAnswers) {
          return currentAnswer.length >= this.currentQuestion.minAnswers;
        }
        return currentAnswer.length > 0;
      } else if (typeof currentAnswer === 'object' && currentAnswer !== null) {
        if (this.currentQuestion.type === 'matching') {
          return Object.keys(currentAnswer).length === this.currentQuestion.pairs.length;
        }
        if (this.currentQuestion.type === 'emotion_recognition') {
          // Для вопросов распознавания эмоций проверяем, что выбраны все эмоции
          const emotionsCount = this.currentQuestion.emotions ? this.currentQuestion.emotions.length : 0;
          return Object.keys(currentAnswer).length === emotionsCount;
        }
        return Object.keys(currentAnswer).length > 0;
      } else {
        return currentAnswer !== undefined && currentAnswer !== null;
      }
    },
    feedbackMascotUrl() {
      if (!this.currentQuestionFeedback) return DEFAULT_MASCOT_URL;
      return this.currentQuestionFeedback.isCorrect
        ? 'https://s3.twcstorage.ru/96a5fb50-69d073b0-a7b3-42f3-975d-79ba376843fd/mascot/happy.png'
        : 'https://s3.twcstorage.ru/96a5fb50-69d073b0-a7b3-42f3-975d-79ba376843fd/mascot/unhappy.png';
    },

    progressBarWidth() {
      if (this.quizData && this.quizData.questions && this.quizData.questions.length > 0) {
        const width = ((this.currentQuestionIndex + 1) / this.quizData.questions.length * 100);
        return width + '%';
      }
      return '0%';
    },
    fixedEmotionImages() {
      if (this.selectedCourseType === 'dogs') {
        return [
          '/static/img/quiz-2/dog-calm-1.jpg',
          '/static/img/quiz-2/dog-playfull-1.jpg',
          '/static/img/quiz-2/dog-aggressive-1.jpg',
          '/static/img/quiz-2/dog-guarding-1.jpg',

        ];
      } else {
        // По умолчанию для кошек (cats) или любого другого типа
        return [
          '/static/img/quiz-2/cat-happy-1.jpg',
          '/static/img/quiz-2/cat-calm-1.jpg',
          '/static/img/quiz-2/cat-angry-1.jpg',
          '/static/img/quiz-2/cat-aggressive-1.jpg'
        ];
      }
    },
    canGoBack() {
      // Кнопка "Назад" показывается только для вопросов распознавания эмоций
      // и только когда пользователь выбрал хотя бы одну эмоцию
      if (this.currentQuestion && this.currentQuestion.type === 'emotion_recognition') {
        const currentAnswer = this.userAnswers[String(this.currentQuestion.id)];
        return currentAnswer && typeof currentAnswer === 'object' && Object.keys(currentAnswer).length > 0;
      }
      return false;
    }
  },

  methods: {

    ensureGuestId() {
      // Теперь просто обновляем userId если он изменился
      this.userId = getOrCreateDeviceUUID();
    },

    sendProgress() {
      this.ensureGuestId();
      axios.post('/api/quiz/progress', {
        user_id: this.userId,
        quiz_id: this.quizRef,
        answered_questions: this.answeredQuestions
      });
    },

    handleAnswerSelected(questionId, answer) {
      if (!this.answeredQuestions.includes(questionId)) {
        this.answeredQuestions.push(questionId);
      }
      this.userAnswers[questionId] = answer;
      this.sendProgress();
    },

    handleAnswersUpdated(questionId, answersArray) {
      if (!this.answeredQuestions.includes(questionId)) {
        this.answeredQuestions.push(questionId);
      }
      this.userAnswers[questionId] = answersArray;
      this.sendProgress();
    },

    handleAutoCheckAnswer(questionId, answer) {
      // Сначала сохраняем ответ
      this.handleAnswerSelected(questionId, answer);

      // Затем автоматически проверяем ответ и показываем модальное окно
      setTimeout(() => {
        if (this.isAnswerProvidedForCurrentQuestion) {
          this.checkCurrentAnswer();
          this.showFeedbackModal();
        }
      }, 100); // Небольшая задержка для обновления состояния
    },

    async fetchQuizData(quizRefToLoad) {
      if (!quizRefToLoad) {
        this.error = "Не указан идентификатор квиза для загрузки.";
        return;
      }
      this.loading = true;
      this.error = null;
      this.quizData = null;
      this.userAnswers = {};
      this.answeredQuestions = [];
      this.showResults = false;
      this.submissionDetails = null;
      this.isFeedbackVisible = false;
      this.isFeedbackMounted = false;
      this.isFeedbackActive = false;
      this.currentQuestionFeedback = null;
      this.mascotImage = DEFAULT_MASCOT_URL;

      // Сообщаем начальный прогресс (0 из 0, пока данные не загружены)
      this.$emit('progress-updated', { current: 0, total: 0 });

      try {
        const response = await axios.get(`/api/quiz/${quizRefToLoad}`);
        this.quizData = response.data;

        if (this.quizData && this.quizData.mascotImage) {
          this.mascotImage = this.quizData.mascotImage;
        }

        if (this.quizData && this.quizData.questions) {
          this.quizData.questions.forEach(q => {
            if (q.type === 'multiple_choice_text' ||
                q.type === 'multiple_choice' ||
                q.type === 'fill_in_the_blanks' ||
                q.type === 'multiple_choice_paw') {
              this.userAnswers[String(q.id)] = [];
            } else if (q.type === 'matching') {
              this.userAnswers[String(q.id)] = {};
            } else if (q.type === 'emotion_recognition') {
              this.userAnswers[String(q.id)] = {};
            } else {
              this.userAnswers[String(q.id)] = null;
            }
          });
        } else {
          this.error = "Получены некорректные данные для квиза.";
          this.quizData = null;
        }

        // Сообщаем актуальный прогресс после загрузки
        if (this.quizData && this.quizData.questions && this.quizData.questions.length > 0) {
          this.$emit('progress-updated', { current: this.currentQuestionIndex + 1, total: this.quizData.questions.length });
        } else {
          // Если вопросов нет, сообщаем 0 из 0
          this.$emit('progress-updated', { current: 0, total: 0 });
          this.error = 'Квиз не содержит вопросов.';
        }
      } catch (err) {
        this.error = err.response?.data?.detail || err.message || "Не удалось загрузить данные квиза.";
        // Сообщаем 0 из 0 в случае ошибки
        this.$emit('progress-updated', { current: 0, total: 0 });
      } finally {
        this.loading = false;
      }
    },
    // (удалены дубликаты handleAnswerSelected и handleAnswersUpdated)

    nextQuestionOrSubmit() {
      if (!this.currentQuestion) return;

      if (!this.isFeedbackVisible) { 
        if (!this.isAnswerProvidedForCurrentQuestion) {
          return;
        }
        this.checkCurrentAnswer();
        this.showFeedbackModal();
      } else {
        // Отслеживаем клик кнопки "Далее" с аналитикой
        this.trackNextButtonClick();
        
        this.hideFeedbackModal();

        if (this.currentQuestionIndex < this.quizData.questions.length - 1) {
          const newIndex = this.currentQuestionIndex + 1;
          this.$emit('question-index-updated', newIndex);
          // Сообщаем обновленный прогресс
          this.$emit('progress-updated', { current: newIndex + 1, total: this.quizData.questions.length });
        } else {
          this.submitResults();
        }
      }
    },
    checkCurrentAnswer() {
      const question = this.currentQuestion;
      if (!question) return;

      let userAnswer = this.userAnswers[String(question.id)];

      let isCorrect = false;
      let explanation = "";

      // Проверяем наличие пояснений в explanations
      if (question.explanations) {
        explanation = isCorrect ? question.explanations.correct : question.explanations.incorrect;
      } else if (question.explanation) {
        explanation = question.explanation;
      }

      // Ensure userAnswer is in a consistent format (array for multiple choice)
      let userAnswerArray = [];
      if (Array.isArray(userAnswer)) {
        userAnswerArray = [...userAnswer].sort((a, b) => a - b); // Sort for consistent comparison
      } else if (userAnswer !== undefined && userAnswer !== null) {
        userAnswerArray = [userAnswer]; // For single choice, wrap in array
      }

      if (question.type === 'single_choice' || question.type === 'single_choice_text') {
        const correctOption = question.correct_option;
        isCorrect = userAnswer === correctOption;

        // Обновляем пояснение после определения правильности ответа
        if (question.explanations) {
          explanation = isCorrect ? question.explanations.correct : question.explanations.incorrect;
        }
      } else if (question.type === 'true_false') {
        const correctAnswerIndex = question.correct_answer ? 1 : 0;
        isCorrect = userAnswer === correctAnswerIndex;

        if (question.explanations) {
          explanation = isCorrect ? question.explanations.correct : question.explanations.incorrect;
        }
      } else if (question.type === 'multiple_choice_text' || question.type === 'multiple_choice' || question.type === 'multiple_choice_paw') {
        // Logic for multiple choice questions
        let correctOptionsFromData;
        if (question.correct_options) {
          correctOptionsFromData = [...question.correct_options].sort((a, b) => a - b);
        } else if (question.options && question.options.every(opt => typeof opt.isCorrect === 'boolean')) {
          correctOptionsFromData = question.options
            .map((opt, index) => opt.isCorrect ? index : -1)
            .filter(index => index !== -1)
            .sort((a, b) => a - b);
        } else {
          correctOptionsFromData = [];
        }

        // Для multiple_choice_image не проверяем количество ответов, только правильность
        if (question.type === 'multiple_choice') {
          isCorrect = userAnswerArray.length === correctOptionsFromData.length &&
                      userAnswerArray.every(ans => correctOptionsFromData.includes(ans)) &&
                      correctOptionsFromData.every(corrAns => userAnswerArray.includes(corrAns));
          // Обновляем пояснение после определения правильности ответа
          if (question.explanations) {
            explanation = isCorrect ? question.explanations.correct : question.explanations.incorrect;
          }
        } else if (correctOptionsFromData.length > 0) {
          isCorrect = userAnswerArray.length === correctOptionsFromData.length &&
                      userAnswerArray.every(ans => correctOptionsFromData.includes(ans)) &&
                      correctOptionsFromData.every(corrAns => userAnswerArray.includes(corrAns));
          // Обновляем пояснение после определения правильности ответа
          if (question.explanations) {
            explanation = isCorrect ? question.explanations.correct : question.explanations.incorrect;
          }
        }
      } else if (question.type === 'emotion_recognition') {
        // userAnswer — объект: { 0: 'Агрессивна', 1: 'Сердится', ... }
        if (typeof userAnswer === 'object' && userAnswer !== null && question.emotions && Array.isArray(question.emotions)) {
          let allCorrect = true;
          for (let i = 0; i < question.emotions.length; i++) {
            const correct = question.emotions[i]?.correct_emotion;
            const selected = userAnswer[i];
            if (selected !== correct) {
              allCorrect = false;
              break;
            }
          }
          isCorrect = allCorrect;
          if (question.explanations) {
            explanation = isCorrect ? question.explanations.correct : question.explanations.incorrect;
          }
        } else {
          isCorrect = false;
          explanation = "Проверка этого ответа будет выполнена на сервере.";
        }
      } else if (question.type === 'matching') {
        // Проверка для matching вопросов
        // userAnswer это объект вида: { "0": 0, "1": 1, "2": 2, "3": 3 }
        // где ключ - leftIndex, значение - rightIndex
        // Правильный ответ когда leftIndex === rightIndex (элементы из одной пары)
        if (typeof userAnswer === 'object' && userAnswer !== null && question.pairs) {
          let allCorrect = true;
          const answeredCount = Object.keys(userAnswer).length;

          // Проверяем что все пары соединены
          if (answeredCount !== question.pairs.length) {
            allCorrect = false;
          } else {
            // Проверяем правильность каждого соединения
            for (const leftIndex in userAnswer) {
              const rightIndex = userAnswer[leftIndex];
              // Правильное соединение когда индексы совпадают (элементы из одной пары)
              if (parseInt(leftIndex) !== parseInt(rightIndex)) {
                allCorrect = false;
                break;
              }
            }
          }

          isCorrect = allCorrect;
          if (question.explanations) {
            explanation = isCorrect ? question.explanations.correct : question.explanations.incorrect;
          }
        } else {
          isCorrect = false;
          explanation = "Соедините все элементы для проверки ответа.";
        }
      } else if (question.type === 'fill_in_the_blanks') {
        if (Array.isArray(userAnswer) && Array.isArray(question.correct_answers) && userAnswer.length === question.correct_answers.length) {
          isCorrect = userAnswer.every((ans, i) => {
            const correctForBlank = question.correct_answers[i];
            if (Array.isArray(correctForBlank)) {
              // Сравниваем с любым вариантом (без учёта регистра и пробелов)
              return correctForBlank.some(
                c => c.toLowerCase().trim() === (ans || '').toLowerCase().trim()
              );
            } else {
              return (ans || '').toLowerCase().trim() === (correctForBlank || '').toLowerCase().trim();
            }
          });
          if (question.explanations) {
            explanation = isCorrect ? question.explanations.correct : question.explanations.incorrect;
          }
        } else {
          isCorrect = false;
          explanation = "Проверьте, что все пропуски заполнены.";
        }
      } else {
        isCorrect = false;
      }

      this.currentQuestionFeedback = {
        questionId: question.id,
        isCorrect: isCorrect,
        explanation: explanation,
      };
    },
    async submitResults() {
      if (!this.quizData || !this.quizData.questions) {
        return;
      }

      const answersToSubmit = {};
      Object.keys(this.userAnswers).forEach(questionId => {
        const userAnswer = this.userAnswers[questionId];

        // Проверяем, что ответ действительно предоставлен
        let hasValidAnswer = false;

        if (Array.isArray(userAnswer)) {
          hasValidAnswer = userAnswer.length > 0 && userAnswer.some(ans => ans !== null && ans !== undefined && ans !== '');
          if (hasValidAnswer) {
            answersToSubmit[questionId] = userAnswer.map(ans =>
              ans !== null && ans !== undefined ? ans.toString() : ''
            ).filter(ans => ans !== ''); // Убираем пустые строки
          }
        } else if (typeof userAnswer === 'object' && userAnswer !== null) {
          hasValidAnswer = Object.keys(userAnswer).length > 0;
          if (hasValidAnswer) {
            answersToSubmit[questionId] = userAnswer;
          }
        } else if (userAnswer !== null && userAnswer !== undefined && userAnswer !== '') {
          hasValidAnswer = true;
          answersToSubmit[questionId] = userAnswer.toString();
        }


      });


      this.submitting = true;
      this.submitError = null;

      // Перед отправкой результатов, убедимся, что прогресс показывает 100% (если это последний шаг)
      if (this.quizData && this.quizData.questions) {
         this.$emit('progress-updated', { current: this.quizData.questions.length, total: this.quizData.questions.length });
      }

      try {
        const response = await axios.post(`/api/quiz/submit`, {
          quiz_ref: this.quizRef,
          answers: answersToSubmit,
          topic_id: this.quizData ? this.quizData.topic_id : undefined,
          course_id: this.quizData ? this.quizData.course_id : undefined,
        });
        this.submitting = false;

        if (response.data && !response.data.error) {
          this.submissionDetails = response.data;
          this.finalScore = response.data.score;
          this.showResults = true;
          this.$emit('quiz-topic-completed', this.quizRef);

        } else {
          this.submitError = response.data.error || 'Произошла ошибка при отправке результатов.';
          this.showResults = true; // Still show whatever we can, or an error message

        }
      } catch (error) {
        this.submitting = false;
        this.submitError = 'Произошла ошибка при отправке результатов.';

      }
    },

    // Отслеживание клика кнопки "Далее" 
    trackNextButtonClick() {
      if (!this.currentQuestion || !this.currentQuestionFeedback) return;

      // Вычисляем номер этапа (1, 2, 3, 4, 5)
      let stepNumber = 1;
      
      // Пытаемся определить номер этапа из topic_id
      if (this.quizData && this.quizData.topic_id) {
        stepNumber = parseInt(this.quizData.topic_id) || 1;
      } 
      // Если topic_id недоступен, пытаемся извлечь из quizRef
      else if (this.quizRef) {
        const topicMatch = this.quizRef.match(/topic(\d+)/);
        if (topicMatch) {
          stepNumber = parseInt(topicMatch[1]) || 1;
        }
      }
      // Fallback к старой формуле только если ничего не найдено
      else {
        stepNumber = Math.floor(this.currentQuestionIndex / 3) + 1;
      }
      
      // Вычисляем номер вопроса в этапе
      // Поскольку каждый квиз соответствует одному этапу, номер вопроса - это просто текущий индекс + 1
      const questionNumber = this.currentQuestionIndex + 1;
      
      // Получаем правильность ответа
      const isCorrect = this.currentQuestionFeedback.isCorrect;
      const answerResult = isCorrect ? 'Верно' : 'Неверно';
      
      // Формируем название события
      const eventName = `question_${questionNumber}_quiz${stepNumber}_click_btn`;
      
      // Отправляем событие
      this.sendEventYM(
        eventName,
        {
          [`question_${questionNumber}_quiz${stepNumber}_param`]: answerResult
        }
      );
    },

    resetQuiz() {
      this.fetchQuizData(this.quizRef);
    },
    showFeedbackModal() {
      // Шаг 1: Монтируем модальное окно в DOM
      this.isFeedbackMounted = true;
      this.isFeedbackVisible = true;

      // Шаг 2: Через небольшую задержку добавляем класс active для анимации
      this.$nextTick(() => {
        setTimeout(() => {
          this.isFeedbackActive = true;
        }, 50); // Небольшая задержка для рендеринга
      });
    },
    hideFeedbackModal() {
      // Сначала убираем класс active
      this.isFeedbackActive = false;

      // Затем через время анимации убираем из DOM
      setTimeout(() => {
        this.isFeedbackVisible = false;
        this.isFeedbackMounted = false;
        this.currentQuestionFeedback = null;
      }, 300); // Время должно совпадать с длительностью анимации
    },
    handleBackButton() {
      // Вызываем метод отмены последнего выбора в дочернем компоненте
      if (this.currentQuestion && this.currentQuestion.type === 'emotion_recognition') {
        // Получаем дочерний компонент через ref
        const emotionComponent = this.$refs.currentQuestionComponent;
        
        if (emotionComponent && typeof emotionComponent.undoLastSelection === 'function') {
          emotionComponent.undoLastSelection();
        }
      }
    }
  }
}
</script>

<style lang="scss">
.quiz-button.quiz-button--back {
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;

  & .btn {
    flex-grow: 1;
  }
}
</style>
